import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useFamily } from '../../contexts/FamilyContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../ui/Button'
import toast from 'react-hot-toast'

const { FiX, FiTrash2, FiAlertTriangle } = FiIcons

const DeleteTaskModal = ({ isOpen, onClose, task }) => {
  const { deleteTask } = useFamily()
  const [loading, setLoading] = useState(false)

  const handleDelete = async () => {
    if (!task) return

    setLoading(true)
    
    try {
      const { error } = await deleteTask(task.id)
      
      if (error) {
        console.error('Task deletion error:', error)
        toast.error(`Failed to delete task: ${error.message || 'Unknown error'}`)
      } else {
        toast.success('Task deleted successfully! 🗑️')
        onClose()
      }
    } catch (error) {
      console.error('Unexpected error deleting task:', error)
      toast.error('An unexpected error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (!task) return null

  const getTaskIcon = (category) => {
    const icons = {
      'cleaning': '🧹',
      'dishes': '🍽️',
      'laundry': '👕',
      'pets': '🐕',
      'homework': '📚',
      'outdoor': '🌱',
      'cooking': '👨‍🍳',
      'organization': '📦',
      'other': '⭐'
    }
    return icons[category] || '⭐'
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative bg-white rounded-2xl shadow-xl w-full max-w-md"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiAlertTriangle} className="text-red-600 text-xl" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Delete Task</h2>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="p-2"
                >
                  <SafeIcon icon={FiX} className="text-xl" />
                </Button>
              </div>

              {/* Task Preview */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border-l-4 border-red-500">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">{getTaskIcon(task.category)}</span>
                  <div>
                    <h3 className="font-semibold text-gray-900">{task.title}</h3>
                    {task.description && (
                      <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span>⭐ {task.points} points</span>
                  <span>📂 {task.category}</span>
                  {task.recurring_pattern !== 'once' && (
                    <span>🔄 {task.recurring_pattern}</span>
                  )}
                </div>
              </div>

              {/* Warning Message */}
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <SafeIcon icon={FiAlertTriangle} className="text-red-600 text-lg mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-red-800 mb-1">
                      Are you sure you want to delete this task?
                    </h4>
                    <ul className="text-sm text-red-700 space-y-1">
                      <li>• This action cannot be undone</li>
                      <li>• The task will be permanently removed</li>
                      <li>• Completion history will be preserved</li>
                      <li>• Children will no longer see this task</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  variant="ghost"
                  onClick={onClose}
                  className="flex-1"
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={handleDelete}
                  loading={loading}
                  className="flex-1"
                >
                  <SafeIcon icon={FiTrash2} className="text-sm" />
                  {loading ? 'Deleting...' : 'Delete Task'}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

export default DeleteTaskModal